// Azure EOL - End-of-Life / Retirement Information
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import '../../App.css';
import './AzureEOL.css';
import Footer from '../Footer';

const EOL_URL = 'https://raw.githubusercontent.com/Azure/EOL/main/service_list.json';

const STATUS = {
  RETIRED: 'Retired',
  UPCOMING: 'Upcoming',
  TBD: 'Date TBD',
};

function getStatus(retirementDate) {
  if (!retirementDate) return STATUS.TBD;
  return new Date(retirementDate) < new Date() ? STATUS.RETIRED : STATUS.UPCOMING;
}

const STATUS_CONFIG = {
  [STATUS.RETIRED]: { color: '#FF6B6B', bg: 'rgba(255,107,107,0.15)', border: 'rgba(255,107,107,0.4)', icon: 'fa-times-circle' },
  [STATUS.UPCOMING]: { color: '#FFC107', bg: 'rgba(255,193,7,0.15)', border: 'rgba(255,193,7,0.4)', icon: 'fa-clock' },
  [STATUS.TBD]: { color: '#888', bg: 'rgba(136,136,136,0.15)', border: 'rgba(136,136,136,0.4)', icon: 'fa-question-circle' },
};

export default function AzureEOL() {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortField, setSortField] = useState('RetirementDate');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(EOL_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Enrich each item with a computed status
      setData(json.map(item => ({ ...item, _status: getStatus(item.RetirementDate) })));
    } catch (err) {
      console.error('Error fetching Azure EOL data:', err);
      setError('Failed to load Azure EOL data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // --- Counts for tab badges ---
  const counts = useMemo(() => {
    const c = { All: data.length, [STATUS.RETIRED]: 0, [STATUS.UPCOMING]: 0, [STATUS.TBD]: 0 };
    data.forEach(x => c[x._status] = (c[x._status] || 0) + 1);
    return c;
  }, [data]);

  // --- Filter + search + sort ---
  const filtered = useMemo(() => {
    let items = data;
    if (activeFilter !== 'All') items = items.filter(x => x._status === activeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(x =>
        x.ServiceName.toLowerCase().includes(q) ||
        x.RetiringFeature.toLowerCase().includes(q)
      );
    }
    // Sort
    items = [...items].sort((a, b) => {
      let va = a[sortField] || '';
      let vb = b[sortField] || '';
      if (sortField === 'RetirementDate') {
        va = va ? new Date(va).getTime() : (sortAsc ? Infinity : -Infinity);
        vb = vb ? new Date(vb).getTime() : (sortAsc ? Infinity : -Infinity);
        return sortAsc ? va - vb : vb - va;
      }
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return items;
  }, [data, activeFilter, search, sortField, sortAsc]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(p => !p);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <i className="fas fa-sort az-eol-sort-icon muted"></i>;
    return <i className={`fas fa-sort-${sortAsc ? 'up' : 'down'} az-eol-sort-icon active`}></i>;
  };

  // --- Loading state ---
  if (loading) {
    return (
      <div className="az-eol-container">
        <div className="az-eol-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading Azure EOL data...</p>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="az-eol-container">
        <div className="az-eol-error-state">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={fetchData} className="btn-retry">
            <i className="fas fa-sync"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  // --- Main render ---
  return (
    <>
      <div className="az-eol-container">

        {/* Header */}
        <div className="az-eol-header">
          <button onClick={() => history.push('/tools')} className="btn-back-inline">
            <i className="fas fa-arrow-left"></i> Back to Tools
          </button>
          <h1><i className="fab fa-microsoft"></i> Azure Updates/EOL</h1>
          <p className="subtitle">Retirement dates and lifecycle information for Azure services and features</p>
        </div>

        {/* Stats row */}
        <div className="az-eol-stats">
          <div className="az-eol-stat az-eol-stat-total">
            <span className="stat-num">{counts.All}</span>
            <span className="stat-label">Total Services</span>
          </div>
          <div className="az-eol-stat az-eol-stat-upcoming">
            <span className="stat-num">{counts[STATUS.UPCOMING] || 0}</span>
            <span className="stat-label">Upcoming</span>
          </div>
          <div className="az-eol-stat az-eol-stat-retired">
            <span className="stat-num">{counts[STATUS.RETIRED] || 0}</span>
            <span className="stat-label">Retired</span>
          </div>
          <div className="az-eol-stat az-eol-stat-tbd">
            <span className="stat-num">{counts[STATUS.TBD] || 0}</span>
            <span className="stat-label">Date TBD</span>
          </div>
        </div>

        {/* Info banner */}
        <div className="az-eol-info-banner">
          <i className="fas fa-info-circle"></i>
          <p>
            Data sourced from the{' '}
            <a href="https://github.com/Azure/EOL" target="_blank" rel="noopener noreferrer">
              Azure EOL GitHub repository
            </a>. Showing {filtered.length} of {data.length} entries.
          </p>
        </div>

        {/* Controls */}
        <div className="az-eol-controls">
          <div className="az-eol-search-wrapper">
            <i className="fas fa-search"></i>
            <input
              type="text"
              className="az-eol-search"
              placeholder="Search services or features..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="az-eol-search-clear" onClick={() => setSearch('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="az-eol-filter-tabs">
            {['All', STATUS.UPCOMING, STATUS.RETIRED, STATUS.TBD].map(f => (
              <button
                key={f}
                className={`az-eol-filter-tab${activeFilter === f ? ' active' : ''}`}
                data-status={f}
                onClick={() => setActiveFilter(f)}
              >
                {f}
                <span className="az-eol-tab-count">{counts[f] ?? 0}</span>
              </button>
            ))}
          </div>

          <button onClick={fetchData} className="btn-refresh">
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="az-eol-empty">
            <i className="fas fa-search"></i>
            <p>No results found for your search.</p>
          </div>
        ) : (
          <div className="az-eol-table-wrapper">
            <table className="az-eol-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('ServiceName')} className="sortable">
                    Service <SortIcon field="ServiceName" />
                  </th>
                  <th onClick={() => handleSort('RetiringFeature')} className="sortable">
                    Retiring Feature <SortIcon field="RetiringFeature" />
                  </th>
                  <th onClick={() => handleSort('RetirementDate')} className="sortable">
                    Retirement Date <SortIcon field="RetirementDate" />
                  </th>
                  <th>Status</th>
                  <th>Migration Available</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const cfg = STATUS_CONFIG[item._status];
                  const days = item.RetirementDate ? daysUntil(item.RetirementDate) : null;
                  return (
                    <tr key={item.Id} className={`az-eol-row az-eol-row-${item._status.toLowerCase().replace(' ', '-')}`}>
                      <td className="az-eol-service">
                        <i className="fab fa-microsoft az-eol-service-icon"></i>
                        {item.ServiceName}
                      </td>
                      <td className="az-eol-feature">{item.RetiringFeature}</td>
                      <td className="az-eol-date">
                        {item.RetirementDate ? (
                          <>
                            <span>{formatDate(item.RetirementDate)}</span>
                            {item._status === STATUS.UPCOMING && days !== null && days <= 180 && (
                              <span className="az-eol-days-badge">
                                {days <= 0 ? 'Today' : `${days}d`}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="az-eol-tbd">TBD</span>
                        )}
                      </td>
                      <td>
                        <span
                          className="az-eol-status-badge"
                          style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                        >
                          <i className={`fas ${cfg.icon}`}></i> {item._status}
                        </span>
                      </td>
                      <td className="az-eol-migration">
                        {item['Availability under Impacted Services'] === 'Yes' ? (
                          <span className="az-eol-avail-yes"><i className="fas fa-check-circle"></i> Yes</span>
                        ) : item['Availability under Impacted Services'] === 'No' ? (
                          <span className="az-eol-avail-no"><i className="fas fa-times-circle"></i> No</span>
                        ) : (
                          <span className="az-eol-avail-na">—</span>
                        )}
                      </td>
                      <td>
                        {item.Link ? (
                          <a href={item.Link} target="_blank" rel="noopener noreferrer" className="az-eol-link">
                            <i className="fas fa-external-link-alt"></i> View
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>
      <Footer />
    </>
  );
}
